import { createContext, useContext, useEffect, useState } from "react";
import { translations } from "@/i18n/translations";

const LanguageContext = createContext(null);
const LS_KEY = "xd_lang";
// Only the languages currently exposed in the header language switcher.
// Auto-detect maps FR/ES browsers to EN, so a French visitor still lands in a
// language they can read instead of being forced to Italian.
const SUPPORTED = new Set(["it", "en", "de"]);

/**
 * Detects the visitor's preferred UI language.
 * Priority: 1) explicit choice saved in localStorage, 2) URL query `?lang=xx`,
 * 3) `navigator.language`, 4) fallback to `it` (default market).
 * The first-visit detected value is persisted so subsequent loads honour it.
 */
function detectInitialLang() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && SUPPORTED.has(saved)) return saved;
    // URL override wins over browser default so campaign links can force a language
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q && SUPPORTED.has(q.toLowerCase())) {
      const v = q.toLowerCase();
      localStorage.setItem(LS_KEY, v);
      return v;
    }
    const nav = (navigator.language || navigator.userLanguage || "it").slice(0, 2).toLowerCase();
    if (SUPPORTED.has(nav)) {
      localStorage.setItem(LS_KEY, nav);
      return nav;
    }
    // Non-supported EU browsers (fr/es/pt/etc.) → default to English so the
    // visitor still lands on a language they can read.
    const isEuButNotIt = nav && nav !== "it";
    if (isEuButNotIt) {
      localStorage.setItem(LS_KEY, "en");
      return "en";
    }
  } catch { /* SSR or storage disabled */ }
  return "it";
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(detectInitialLang);

  useEffect(() => { try { localStorage.setItem(LS_KEY, lang); } catch { /* ignore */ } }, [lang]);

  const t = (key) => {
    const dict = translations[lang] || translations.it;
    return dict[key] || translations.it[key] || key;
  };

  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
}

export function useLang() { return useContext(LanguageContext); }
